import React from 'react';
import { Link } from 'react-router-dom';
import { Send, Youtube, Music2 } from 'lucide-react';
import { BrandLogo } from '@/components/BrandMark';
import { EXAM_YEAR, FOOTER_AI_DISCLAIMER, BRAND_TAGLINE } from '@/lib/siteCopy';
import { EDUCATOR_SUITE_ENABLED } from '@/lib/educators';
import { EducatorSuiteSoftNavButton } from '@/components/educator/EducatorSuiteComingSoon';
import { Reveal, RevealStagger } from '@/lib/motion';

const linkClass =
  'text-[13px] text-muted-foreground hover:text-foreground transition-colors duration-2 inline-flex items-center gap-1';

const SocialLink = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-10 h-10 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/25 flex items-center justify-center transition-colors duration-2"
    aria-label={label}
  >
    {children}
  </a>
);

const Column = ({ title, children }) => (
  <div>
    <h4 className="protocol-label uppercase tracking-[0.1em] text-[11px] mb-5">{title}</h4>
    <ul className="space-y-3">{children}</ul>
  </div>
);

export const Footer = () => {
  return (
    <footer className="relative mt-24 md:mt-36 border-t border-border">
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12 md:pb-16">
          <Reveal variant="soft" className="mb-12 md:mb-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
              {/* Brand */}
              <div className="md:col-span-4 space-y-5">
                <Link to="/" className="navbar-logo inline-flex items-center">
                  <BrandLogo markSize={30} textClassName="text-2xl" />
                </Link>
                <p className="text-[14px] text-muted-foreground leading-relaxed max-w-sm tracking-apple-regular">
                  {BRAND_TAGLINE}
                </p>
                <RevealStagger className="flex gap-3 pt-2">
                  <SocialLink href="https://t.me/reshai_official" label="Telegram">
                    <Send size={17} />
                  </SocialLink>
                  <SocialLink href="https://youtube.com/@reshai_official" label="YouTube">
                    <Youtube size={17} />
                  </SocialLink>
                  <SocialLink href="https://tiktok.com/@reshai_official" label="TikTok">
                    <Music2 size={17} />
                  </SocialLink>
                </RevealStagger>
              </div>

              {/* Columns */}
              <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-8">
                <Column title="Продукт">
                  <li>
                    <Link to="/#how-it-works" className={linkClass}>
                      Как это работает
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className={linkClass}>
                      Подписка
                    </Link>
                  </li>
                  <li>
                    {EDUCATOR_SUITE_ENABLED ? (
                      <Link to="/for-tutors" className={linkClass}>
                        Для репетиторов
                      </Link>
                    ) : (
                      <EducatorSuiteSoftNavButton
                        dialogVariant="nav_marketing"
                        className="text-[13px] text-muted-foreground hover:text-foreground transition-colors duration-md ease-apple p-0 h-auto min-h-0 font-normal bg-transparent border-0 shadow-none rounded-none justify-start"
                      >
                        Для репетиторов
                      </EducatorSuiteSoftNavButton>
                    )}
                  </li>
                  <li>
                    {EDUCATOR_SUITE_ENABLED ? (
                      <Link to="/for-parents" className={linkClass}>
                        Для родителей
                      </Link>
                    ) : (
                      <span className="text-[13px] text-muted-foreground/55">Для родителей — скоро</span>
                    )}
                  </li>
                  <li>
                    <Link to="/referral" className={linkClass}>
                      Реферальная программа
                    </Link>
                  </li>
                </Column>

                <Column title="Документы">
                  <li>
                    <Link to="/terms" className={linkClass}>
                      Условия использования
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className={linkClass}>
                      Конфиденциальность
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookies" className={linkClass}>
                      Cookies
                    </Link>
                  </li>
                  <li>
                    <Link to="/refund" className={linkClass}>
                      Возврат средств
                    </Link>
                  </li>
                </Column>

                <Column title="Контакты">
                  <li>
                    <a href="mailto:support@reshai.xyz" className={linkClass}>
                      support@reshai.xyz
                    </a>
                  </li>
                  <li>
                    <Link to="/contact" className={linkClass}>
                      Форма обратной связи
                    </Link>
                  </li>
                </Column>
              </div>
            </div>
          </Reveal>

          <div className="border-t border-border" aria-hidden />

          <div className="mt-8 flex flex-col md:flex-row justify-between items-start gap-6 text-[12px] text-muted-foreground/85">
            <p>© {EXAM_YEAR} РешAI. Все права защищены.</p>
            <p className="max-w-2xl md:text-right leading-relaxed text-muted-foreground/70">
              {FOOTER_AI_DISCLAIMER}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
